<template>
  <v-container
    id="dashboard"
    fluid
    tag="section"
  >
    <v-row>
      <!-- QUERY DATE-RANGE -->
      <v-col cols="12" lg="5">
        <v-row>
          <v-col>
            <DateRangePicker @update="onDateRangeUpdated"
              :def-dates="dates"
              :shortcuts="['last-week', 'this-week', 'yesterday', 'today']">
            </DateRangePicker>
          </v-col>
          <v-col>
            <ScopeSelector vertical
              @org-selected="onOrganizationSelected"
              @mch-selected="onMerchantSelected">
            </ScopeSelector>
          </v-col>
        </v-row>
      </v-col>

      <!-- SEARCH -->
      <v-col cols="12" sm="6" lg="4">
        <v-card class="my-0" style="height: 100%;">
          <v-card-text>
            <v-container>
              <v-row align="end">
                <v-col cols="12" class="pa-0">
                  <v-radio-group row v-model="form.search.searchBy">
                    <template v-slot:label>
                      <div>{{ $t('views.orders.searchBy') }}<br></div>
                    </template>
                    <v-radio :label="$t('views.orders.ddpayOrderId')" value="ddpay_order_id"></v-radio>
                    <v-radio :label="$t('views.orders.merchantOrderId')" value="merchant_order_number"></v-radio>
                    <v-radio :label="$t('views.orders.amount')" value="amount"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col cols="12" class="pa-0">
                  <v-form @submit.native.prevent @submit="search">
                    <v-text-field outlined prepend-icon="mdi-magnify"
                      v-model="form.search.keyword"
                      placeholder="(Press ENTER to search)" clearable>
                    </v-text-field>
                  </v-form>
                </v-col>
              </v-row>
            </v-container>  
          </v-card-text>
        </v-card>
          
      </v-col>

      <v-col cols="12" sm="6" lg="2" style="height: 100%;" class="d-flex align-start flex-column">
        <v-btn x-large block color="warning" @click="exportOrderList">
          <v-icon left>mdi-file-export</v-icon>
          {{ $t('views.orders.downloadExcel') }}
        </v-btn>
        <v-btn x-large block color="secondary" @click="exportDDPayReport">
          <v-icon left>mdi-file-export</v-icon>
          {{ $t('views.orders.downloadDDPayReport') }}
        </v-btn>
        <v-btn block large text color="grey darken-3" @click="forceRefresh">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-col>

      <!-- FILTERS -->
      <v-col cols="12" class="pb-0">
        <OrderFilters v-model="form.filter"
          :service-options="ddpayPaymentServiceOptions" :status-options="ddpwOrderStatusOptions"
          :payment-type-options="paymentTypeOptions"
          :mode-options="ddpwPaymentModeOptions"
          :service-label="$t('views.orders.paymentServiceDdpgOnly')">
        </OrderFilters>
      </v-col>

      <!-- LIST OF ORDERS -->
      <v-col cols="12">

        <v-card class="mt-0">
          <v-card-text>

            <SSRTable ref="list-orders"
              :data-url="loadUrl" :data-key="'orders'" :schema="schema"
              @load-error="onLoadError" @load-data="fabs = { }">

              <div slot="status" slot-scope="{item}">
                <v-icon v-if="item.status == 'issued'">mdi-progress-clock</v-icon>
                <v-icon v-else-if="item.status == 'done'" color="success">mdi-check-circle-outline</v-icon>
                <v-icon v-else-if="item.status == 'failed'" color="error">mdi-alert</v-icon>
                <v-icon v-else-if="item.status == 'cancelled'" color="warning">mdi-cancel</v-icon>
                <span v-else>{{ item.status }}</span>
              </div>

              <div slot="identifiers" slot-scope="{item}">
                <b>DDPay:</b> {{ item.ddpw_order_id }}<br>
                <!-- <b>DDPG:</b> {{ item.ddpg_order_id }}<br> -->
                <b>{{ $t('views.orders.shop') }}:</b> {{ item.merchant_order_number }}
              </div>

              <div slot="payment_mode" slot-scope="{item}">
                {{ paymentModeLabel(item.payment_mode) }}
              </div>

              <div slot="payment_type" slot-scope="{item}">
                <!-- <div v-if="getPaymentServiceIcon(item.payment_service)">
                  <img :src="getPaymentServiceIcon(item.payment_service)" style="height: 24px;">
                </div> -->
                <div v-if="getResolvedIcon({
                  service: item.payment_service,
                  type: item.payment_type
                })">
                  <img :src="getResolvedIcon({
                    service: item.payment_service,
                    type: item.payment_type
                    })"
                    style="height: 24px;">
                </div>
                <div v-else>{{ item.payment_service }}</div>
              </div>

              <div slot="acquirer_channel" slot-scope="{item}">
                <v-chip v-if="item.acquirer_channel" x-small
                  :color="acquirerChannelColor(item.acquirer_channel)">
                  {{ acquirerChannelLabel(item.acquirer_channel) }}
                </v-chip>
                <span v-else>—</span>
              </div>

              <div slot="amount" slot-scope="{item}">
                $ {{ Number(item.amount).toLocaleString() }}
              </div>

              <div slot="actions" slot-scope="{item}">
                <v-speed-dial right direction="left" :open-on-hover="false"
                  :value="Boolean(fabs[item.ddpw_order_id])"
                  @input="value => $set(fabs, item.ddpw_order_id, value)">
                  <template v-slot:activator>
                    <v-btn x-small dark fab color="purple darken-2">
                      <v-icon v-if="fabs[item.ddpw_order_id]">
                        mdi-close
                      </v-icon>
                      <v-icon v-else>
                        mdi-dots-horizontal
                      </v-icon>
                    </v-btn>
                  </template>

                  <v-btn fab dark x-small color="indigo"  @click="showInfoDialog(item)">
                    <v-icon>mdi-information-variant</v-icon>
                  </v-btn>
                </v-speed-dial>

              </div>
            </SSRTable>

          </v-card-text>
        </v-card>

      </v-col>
    </v-row>

    <!-- Dialog for showing detail and transactional history of the selected order -->
    <DdpayOrderDetailDialog :show.sync="dialog.info.show"
      :payment-mode="dialog.info.paymentMode"
      :ddpay-order-id="dialog.info.ddpayOrderId">
    </DdpayOrderDetailDialog>
  </v-container>
</template>

<script>
  import SSRTable from '@/components/SSRTable';
  import DateRangePicker from '@/components/DateRangePicker';
  import ScopeSelector from '@/components/ScopeSelector';
  import OrderFilters from '@/components/OrderFilters';
  import { GetIconMixin } from '@/mixins/get-icons';
  import { PaymentFiltersMixin } from '@/mixins/payment-filters';
  import { OrderDateGuardMixin } from '@/mixins/order-date-guard';

  import DdpayOrderDetailDialog from '@/views/dashboard/subviews/DdpayOrderDetailDialog';

  import axios from 'axios';
  import moment from 'moment';

  export default {
    components: {
      SSRTable,
      DateRangePicker,
      ScopeSelector,
      OrderFilters,
      DdpayOrderDetailDialog
    },
    mixins: [
      GetIconMixin,
      PaymentFiltersMixin,
      OrderDateGuardMixin
    ],

    mounted() {

    },

    methods: {
      onDateRangeUpdated(dates) {
        if (!this.guardDateRange(dates)) return;
        this.dates = dates;
        this.revalidateAmountSearch();
      },
      onOrganizationSelected(org) {
        this.scope.organization = org;
        // this.loadStats();
      },
      onMerchantSelected(merchant) {
        this.scope.merchant = merchant;
        this.revalidateAmountSearch();
      },
      // 依金額查詢會掃 ddpw.orders.amount（無索引），故限制單日且需指定店家。
      isAmountSearchAllowed() {
        if (!this.isSingleDay(this.dates)) {
          this.$notify({ text: this.$t('views.orders.amountNeedsSingleDay'), type: 'error' });
          return false;
        }
        if (!this.scope.merchant) {
          this.$notify({ text: this.$t('views.orders.amountNeedsShop'), type: 'error' });
          return false;
        }
        return true;
      },
      // 區間或店家被改成不合規時撤掉金額查詢。keyword 要一起清，
      // 否則搜尋框顯示金額、表格卻載入了未篩選的全部資料。
      revalidateAmountSearch() {
        if (this.form.search.searchBy !== 'amount' || !this.form.search.queryString)
          return;
        if (!this.isAmountSearchAllowed()) {
          this.form.search.queryString = '';
          this.form.search.keyword = '';
        }
      },
      search() {
        let keyword = this.form.search.keyword;
        // 關鍵字為空代表要清掉查詢，不該被金額守衛擋住。
        if (!keyword || !keyword.length) {
          this.form.search.queryString = '';
          return;
        }
        if (this.form.search.searchBy === 'amount' && !this.isAmountSearchAllowed())
          return;

        this.form.search.queryString = `&search=${this.form.search.searchBy}:${encodeURIComponent(keyword)}`;
      },
      forceRefresh() {
        this.$refs['list-orders'].loadData();
      },

      showInfoDialog(order) {
        this.dialog.info.ddpayOrderId = order['ddpw_order_id'];
        this.dialog.info.paymentMode = order['payment_mode'];
        this.dialog.info.show = true;
      },

      exportOrderList() {
        if (!this.guardDateRange(this.dates)) return;
        let from = moment(this.dates[0]).startOf('day');
        let to = moment(this.dates[1]).endOf('day');
        window.open(`${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/ddpay-orders/export?timeFormat=${encodeURIComponent('YYYY/M/D h:mm:ssA')}`
          + `&from=${from.unix()}&to=${to.unix()}`
          + (this.scope.merchant? `&shopId=${this.scope.merchant['merchant_id']}`: '')
          + `${this.filterQueryString}`
          + `${this.form.search.queryString}`);
      },

      exportDDPayReport() {
        if (!this.scope.merchant || !this.scope.merchant['merchant_id']) {
          this.$notify({
            text: this.$t('views.orders.reportNeedsShop'),
            type: 'error'
          });
          return;
        }
        if (!this.guardDateRange(this.dates)) return;
        let from = moment(this.dates[0]).startOf('day');
        let to = moment(this.dates[1]).endOf('day');
        window.open(`${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/orders/export-ddpay-report`
          + `?shop-id=${this.scope.merchant['merchant_id']}`
          + `&from=${from.unix()}&to=${to.unix()}`);
      }
    },

    data() {
      return {

        dates: [
          moment().startOf('day').format('YYYY-MM-DD'),
          moment().format('YYYY-MM-DD')
        ],
        scope: {
          organization: null,
          merchant: null
        },

        // actions slot 每列渲染一次，展開狀態必須逐列記；共用一個屬性會全部列一起展開。
        fabs: { },

        form: {
          search: {
            searchBy: 'ddpay_order_id',
            keyword: '',
            queryString: ''
          },
          filter: {
            service: null,
            payType: null,
            status: null,
            mode: null
          }
        },
        dialog: {
          info: {
            show: false,
            ddpayOrderId: '',
            paymentMode: ''
          }
        },
        expansion: {
          detail: {
            show: false,
            context: { }
          }
        }
      };
    },

    computed: {
      filterQueryString() {
        return this.buildPaymentFilterQuery(this.ddpayPaymentServiceOptions, this.form.filter);
      },

      //NOTE: 必須保持 pure。每次改篩選都會重算，在此 $notify 會洗版、
      //      回 undefined 會讓 SSRTable 靜默清空表格。金額守衛在 search()。
      loadUrl() {
        if (!this.$store.getters.user || this.$store.getters.user.level !== 'superadmin') {
          return null;
        }

        let from = moment(this.dates[0]).startOf('day');
        let to = moment(this.dates[1]).endOf('day');
        return `${process.env.VUE_APP_QRPAY_ADMIN_HOST}/api/ddpay-orders?timeFormat=${encodeURIComponent('YYYY/M/D h:mm:ssA')}`
          + `&from=${from.unix()}&to=${to.unix()}`
          + (this.scope.merchant? `&shopId=${this.scope.merchant['merchant_id']}`: '')
          + `${this.filterQueryString}`
          + `${this.form.search.queryString}`;
      },

      user() {
        return this.$store.getters.user;
      },

      schema() {
        return [
          { value: 'created_at', text: this.$t('views.orders.createdAt'), sortable: true},
          { value: 'shop_name', text: this.$t('views.orders.shop'), attrs: { }, sortable: false },
          { value: 'identifiers', text: this.$t('views.orders.identifiers'), sortable: false },
          { value: 'status', text: this.$t('views.orders.status'), align: 'center', sortable: false },
          { value: 'amount', text: this.$t('views.orders.amount'), align: 'end', width: 120, sortable: false },     
          { value: 'payment_mode', text: this.$t('views.orders.paymentMode'), align: 'center', sortable: false },
          { value: 'payment_type', text: this.$t('views.orders.paymentService'), align: 'center', sortable: false },
          { value: 'acquirer_channel', text: this.$t('views.orders.acquirerChannel'), align: 'center', sortable: false },
          { value: 'actions', text: '', sortable: false }
        ];
      },
      transactionSchema() {
        return [
          { value: 'payment_order_id', text: this.$t('views.orders.dialogs.info.paymentOrderId') },
          { value: 'transaction_id', text: this.$t('views.orders.dialogs.info.transactionId') },
          { value: 'bank_transaction_id', text: this.$t('views.orders.dialogs.info.bankTransactionId') },
          { value: 'amount', text: '$' },
          { value: 'status', text: this.$t('views.orders.dialogs.info.status') },
          { value: 'status_code', text: this.$t('views.orders.dialogs.info.code') },
          { value: 'message', text: this.$t('views.orders.dialogs.info.message') },
          { value: 'created_at', text: this.$t('views.orders.dialogs.info.time') }
        ];
      }
    }
  }
</script>
